import WarningAmber from "@mui/icons-material/WarningAmber";
import { Box, Button, Skeleton, Switch, Typography } from "@mui/material";
import React from "react";

import { colors } from "../../../../../../config/colors";
import type { PastTimeOffRole } from "../controllers/use-past-time-off-roles";
import { isValidLookbackDays } from "../controllers/use-past-time-off-roles";
import RoleAccessRow from "./role-access-row";

interface ShiftSettingsWithPastTimeOff extends Record<string, unknown> {
	pastDatedTimeOffAllowed?: boolean;
}

interface PastDatedTimeOffProps {
	newShiftSettings: ShiftSettingsWithPastTimeOff;
	setNewShiftSettings: React.Dispatch<
		React.SetStateAction<ShiftSettingsWithPastTimeOff>
	>;
	isSettingsLoading: boolean;
	roles: PastTimeOffRole[];
	rolesStatus: "idle" | "pending" | "fulfilled" | "error";
	// The backend's message when saving the roles failed.
	rolesSaveError: string | null;
	onRoleChange: (roleId: number, changes: Partial<PastTimeOffRole>) => void;
	onRetryRoles: () => void;
}

const helperTextSx = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "12px",
	color: "rgba(0, 0, 0, 0.6)",
};

// timeOff.allowPastDates (GAT-7829): lets supervisors mark time off for a
// worker on a date that has already passed, at this location only. When it's
// on, the admin picks which roles may do it and how many days back.
const PastDatedTimeOff: React.FC<PastDatedTimeOffProps> = ({
	newShiftSettings,
	setNewShiftSettings,
	isSettingsLoading,
	roles,
	rolesStatus,
	rolesSaveError,
	onRoleChange,
	onRetryRoles,
}) => {
	if (Object.keys(newShiftSettings).length === 0) return null;

	const isEnabled = newShiftSettings.pastDatedTimeOffAllowed ?? false;
	const isNoRoleAllowed =
		rolesStatus === "fulfilled" &&
		roles.length > 0 &&
		roles.every((role) => role.access === "none");

	const renderRoles = () => {
		if (rolesStatus === "idle" || rolesStatus === "pending") {
			return [0, 1, 2].map((row) => (
				<Skeleton key={row} height={56} />
			));
		}
		if (rolesStatus === "error") {
			return (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						paddingTop: "8px",
					}}
				>
					<WarningAmber sx={{ color: colors.error, fontSize: "16px" }} />
					<Typography sx={{ ...helperTextSx, color: colors.error }}>
						Couldn&apos;t load the roles for this location.
					</Typography>
					<Button size='small' onClick={onRetryRoles}>
						Try again
					</Button>
				</Box>
			);
		}
		if (roles.length === 0) {
			return (
				<Typography sx={{ ...helperTextSx, paddingTop: "8px" }}>
					No supervisor roles found for this location.
				</Typography>
			);
		}
		return roles.map((role, index) => (
			<RoleAccessRow
				key={role.roleId}
				role={role}
				isDaysInvalid={
					role.access === "limited" && !isValidLookbackDays(role.days)
				}
				isLast={index === roles.length - 1}
				onChange={onRoleChange}
			/>
		));
	};

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				padding: "16px",
				marginTop: "32px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Allow past-dated time off
					</Typography>
					<Typography sx={helperTextSx}>
						Lets supervisors mark time off for a worker on a date that has
						already passed. Workers can&apos;t add past-dated time off
						themselves.
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{isSettingsLoading ? (
						<Skeleton width={50} height={40} />
					) : (
						<Switch
							checked={isEnabled}
							inputProps={{ "aria-label": "Allow past-dated time off" }}
							onChange={(e) =>
								setNewShiftSettings((prevSettings) => ({
									...prevSettings,
									pastDatedTimeOffAllowed: e.target.checked,
								}))
							}
						/>
					)}
				</Box>
			</Box>

			{isEnabled ? (
				<Box
					sx={{
						marginTop: "20px",
						borderRadius: "8px",
						border: 1,
						borderWidth: "1px",
						borderColor: "rgba(0,0,0,0.12)",
						background: "#FFF",
						padding: "16px",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 500,
							fontSize: "14px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Who can add past-dated time off
					</Typography>
					<Typography sx={{ ...helperTextSx, marginBottom: "4px" }}>
						Supervisors can pick past dates only as far back as their role
						allows. Someone with more than one role gets the longest window.
					</Typography>
					{renderRoles()}
					{rolesSaveError ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								marginTop: "8px",
							}}
						>
							<WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
							<Typography sx={{ ...helperTextSx, color: colors.error }}>
								{rolesSaveError}
							</Typography>
						</Box>
					) : null}
					{isNoRoleAllowed ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								marginTop: "8px",
							}}
						>
							<WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
							<Typography sx={{ ...helperTextSx, color: colors.error }}>
								No role can add past-dated time off, so it would stay off in
								practice. Allow at least one role to save, or switch it off
								above.
							</Typography>
						</Box>
					) : null}
				</Box>
			) : null}
		</Box>
	);
};

export default PastDatedTimeOff;
